'use client'

import { storyblokEditable } from '@storyblok/react/rsc'
import Image from 'next/image'
import { type FC } from 'react'
import { FormattedMessage } from 'react-intl'

import { IconArrowRight } from '@redteclab/icons'

import { Button } from 'base-ui'

import { useCdnMediaServer } from '../../../cdn'
import { useGlobalConfigContext } from '../../../global-config'
import { urlResolverGetBrandsIndexPage } from '../../../url-handling'
import { cmsStoryblokSlugToPublicUrl } from '../../cmsStoryblokSlugToPublicUrl'
import { type StoryblokComponentProps } from '../../model'
import { type StoryblokBrandCarouselType } from '../../storyblok'
import { useStoryblokServerSideData } from '../../storyblok-server-side-data/context/storyblokServerSideDataContext'
import { CmsSlider, CmsSliderItem } from '../CmsSlider'

export const StoryblokBrandCarousel: FC<
  StoryblokComponentProps<StoryblokBrandCarouselType>
> = ({ blok }) => {
  const publicGlobalConfig = useGlobalConfigContext()
  const { data } = useStoryblokServerSideData(blok)
  const { setMediaServerOrigin } = useCdnMediaServer()

  if (data?.brands.length === 0) {
    return null
  }

  const slides = data?.brands.map((brand) => {
    const { content, full_slug, id, name } = brand

    let imageSrc = content.brandLogo[0]?.legacyImageSrc
    imageSrc = imageSrc ? setMediaServerOrigin(`brand-logos/${imageSrc}`) : ''

    return (
      <CmsSliderItem key={id}>
        <a
          // eslint-disable-next-line tailwindcss/no-arbitrary-value -- don't reference, refactor if touched
          className="block h-[170px] rounded-m border border-dark-primary-strong p-6"
          href={cmsStoryblokSlugToPublicUrl(full_slug)}
        >
          <div className="relative min-h-full min-w-full">
            <Image
              alt={name}
              className="object-contain"
              fill
              src={imageSrc}
              unoptimized
            />
          </div>
        </a>
      </CmsSliderItem>
    )
  })

  return (
    <div className="flex flex-col gap-y-5" {...storyblokEditable(blok)}>
      <div className="flex justify-between gap-5 text-2xl font-medium">
        <FormattedMessage id="general.label.brands" />
        <Button
          endIcon={<IconArrowRight />}
          href={urlResolverGetBrandsIndexPage(publicGlobalConfig)}
          size="large"
          variant="text"
        >
          <FormattedMessage id="general.label.brandsShowAll" />
        </Button>
      </div>
      <CmsSlider
        gap={{ mobile: 'var(--spacing-2)' }}
        itemsPerPage={{ desktop: '4', mobile: '2' }}
      >
        {slides}
      </CmsSlider>
    </div>
  )
}
